import {
  Button,
  ColumnTable,
  Icon,
  OutlinedSelect,
  Table,
} from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { useWarningsSummaryDetailsFormLoaderData } from '../../loaders/StudentWarningsSummaryDetailsFormLoader';
import { useWarningsSummaryDetailsLoader } from '../../loaders/StudentWarningsSummaryDetailsLoader';
import HealthWarningGroupModal, {
  HealthWarningGroupModalMethods,
  HealthWarningGroupRecord,
} from './HealthWarningGroupModal';

const HealthWarningGroup = () => {
  const modalRef = useRef<HealthWarningGroupModalMethods>(null);

  const data = useWarningsSummaryDetailsFormLoaderData();
  const { result, refresh } = useWarningsSummaryDetailsLoader();
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_DETAILS);

  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const items = useMemo(() => {
    if (!result?.success) {
      return [];
    }

    return (result.data?.groupData as HealthWarningGroupRecord[]) ?? [];
  }, [result]);

  const filteredItems = useMemo(() => {
    if (!typeFilter) return items;
    return items.filter((item) => item.type === typeFilter);
  }, [items, typeFilter]);

  const handleOpenModal = useCallback((record?: HealthWarningGroupRecord) => {
    modalRef.current?.open(record);
  }, []);

  const columns = useMemo<ColumnTable<HealthWarningGroupRecord>[]>(
    () => [
      {
        columnName: 'type',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.health.table.type`),
        cellFormat: ({ value }) => {
          const type = data?.healthRecordTypes.find(
            (option) => option.value === value,
          );
          return type ? type.label : value;
        },
      },
      {
        columnName: 'date',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.health.table.date`),
        cellFormat: ({ value }) => {
          const date = dayjs(value);
          return date.utc().format('DD/MM/YYYY');
        },
      },
      {
        columnName: 'state',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.health.table.state`),
        cellFormat: ({ value }) => {
          const state = data?.healthRecordStates.find(
            (option) => option.value === value,
          );
          return state ? state.label : value;
        },
      },
      {
        columnName: 'notes',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.health.table.notes`),
      },
      {
        columnName: 'id',
        tdClassName: 'text-center',
        thClassName: 'text-center',
        headerText: t(`group.health.table.details`),
        cellFormat: ({ row }) => {
          return (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => handleOpenModal(row)}
            >
              <Icon name="eye" />
            </button>
          );
        },
      },
    ],
    [t, data, handleOpenModal],
  );

  return (
    <div className="w-100 d-flex flex-column mt-3" style={{ gap: 12 }}>
      <Row>
        <Col xs={12} md={3}>
          <OutlinedSelect
            name="typeFilter"
            label={t(`group.health.table.type`)}
            options={data?.healthRecordTypes || []}
            isClearable
            value={
              data?.healthRecordTypes.find(
                (option) => option.value === typeFilter,
              ) || undefined
            }
            onChange={(option) => setTypeFilter(option ? option.value : null)}
            disabled={!data?.healthRecordTypes.length}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} md={6}>
          <h2 className="text-primary fw-700 fs-20 mb-0 mt-3">
            {t(`group.health.title`)}
          </h2>
        </Col>
        <Col xs={12} md={6}>
          <div className="d-flex justify-content-end">
            <Button
              text={t(`group.health.actions.downloadReport`)}
              size="md"
              type="submit"
              icon="download"
            />
            <Button
              text={t(`group.health.actions.addRecord`)}
              className="ml-2"
              size="md"
              outlined
              type="button"
              icon="plus"
              onClick={() => handleOpenModal()}
            />
          </div>
        </Col>
      </Row>
      <Table data={filteredItems} columns={columns} />
      <HealthWarningGroupModal ref={modalRef} onSave={refresh} />
    </div>
  );
};

export default HealthWarningGroup;
